import { Entity, Input, Var } from "addeus-common-library/stores/firestore";
import {
    Collection,
    SubCollection,
} from "addeus-common-library/stores/firestore/collection";
import { EntityArray } from "addeus-common-library/stores/firestore/entity";
import {
    SecurityCollectionGroup,
    SecurityEntity,
    SecuritySubCollection,
} from "addeus-common-library/stores/firestore/security/securityDecorators";
import moment from "moment-with-locales-es6";
import { Emojis, Familiarity } from "../utils/profile";
import { Icon } from "./icon";
import { Address } from "./profile/address";
import "./profile/audit";
import type { Audit } from "./profile/audit/index";
import { BusinessHour } from "./profile/businessHour";
import { PlanningGeneration } from "./profile/planningGeneration";
import "./profile/post";
import type { Post } from "./profile/post";
import "./profile/review";
import type { Review } from "./profile/review";
import "./profile/socialProfile";
import type { SocialProfile } from "./profile/socialProfile";
import "./task";
import type { Task } from "./task";
import "./userMetadata";
import type { UserMetadata } from "./userMetadata";

export enum Tone {
    Friendly = "Friendly",
    Motivational = "Motivational",
    Secret = "Secret",
    Professional = "Professional",
    Educational = "Educational",
    Funny = "Funny",
    Interactive = "Interactive",
}

export enum State {
    Active = "Active",
    Inactive = "Inactive",
}

export enum ProfileType {
    Professional = "Professional",
    Personal = "Personal",
}

export enum PlaceType {
    AdministrativeServices = "AdministrativeServices",
    AccountingAndFinance = "AccountingAndFinance",
    RealEstate = "RealEstate",
    TechnicalServices = "TechnicalServices",
    Agriculture = "Agriculture",
    ArtsAndEntertainment = "ArtsAndEntertainment",
    FoodTrade = "FoodTrade",
    CoffeeAndBar = "CoffeeAndBar",
    Construction = "Construction",
    Distribution = "Distribution",
    Leisure = "Leisure",
    SpecialistStores = "SpecialistStores",
    Energy = "Energy",
    Education = "Education",
    Accommodation = "Accommodation",
    Industry = "Industry",
    ITAndCommunication = "ITAndCommunication",
    FoodAndDining = "FoodAndDining",
    HealthAndSocialCare = "HealthAndSocialCare",
    Transport = "Transport",
    Other = "Other",
}

export enum ContentLanguage {
    French = "French",
    English = "English",
}

const isWorkspaceOwner =
    "exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))";
const isWorkspaceUser =
    "exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/users/$(request.auth.uid))";
const isProfileOwner =
    "exists(/databases/$(database)/documents/Profile/$(ProfileId)/owners/$(request.auth.uid))";
const isProfileUser =
    "exists(/databases/$(database)/documents/Profile/$(ProfileId)/users/$(request.auth.uid))";

const isAdmin =
    "get(/databases/$(database)/documents/UserMetadata/$(request.auth.uid)).data.role == 'admin'";

const isWorkspaceOrProfileOwner = `${isWorkspaceOwner} || ${isProfileOwner}`;
const isWorkspaceOrProfileUser = `${isWorkspaceUser} || ${isProfileUser}`;
const isWorkspaceOrProfileOwnerOrUser = `${isWorkspaceOrProfileOwner} || ${isWorkspaceOrProfileUser}`;

@Collection({
    namespace: "Profile",
})
@SecurityEntity({
    get: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
    update: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
    create: isAdmin,
    list: isAdmin,
})
export class Profile extends Entity {
    @Var(String)
    @Input("text", {
        required: true,
    })
    name = "";

    @Var(moment)
    createdAt: moment = moment();

    @Var(moment)
    deletedAt: moment | null = null;

    isNew: boolean = false;

    @Var(State)
    state: State = State.Active;

    @Var(Icon)
    icon?: Icon;

    @Var(ProfileType)
    profileType?: ProfileType;

    @Var(String)
    profession?: string;

    @Var(String)
    website?: string;

    @Var(String)
    email?: string;

    @Var(String)
    phone?: string;

    @Var(String)
    description?: string;

    @Var(moment)
    lastPostNotificationAt?: moment;

    @Var(Array.of(BusinessHour))
    businessHours: BusinessHour[] = EntityArray();

    @Var(Address)
    address?: Address = new Address();

    @Var(String)
    reportingLink?: string;

    @Var(PlanningGeneration)
    planningGeneration?: PlanningGeneration;

    @Var(String)
    activity?: string;

    @Var(String)
    placeName?: string;

    @Var(PlaceType)
    placeType?: PlaceType;

    @Var(String)
    keyStrength?: string;

    @Var(String)
    passion?: string;

    @Var(Familiarity)
    familiarity?: Familiarity;

    @Var(String)
    emojis?: Emojis;

    @Var(Array.of(String))
    tone: Tone[] = [];

    @Var(String)
    note?: string;

    @Var(String)
    communicationObjective?: string;

    @Var(String)
    personalization?: string;

    @Var(String)
    facebookPersonalization?: string;

    @Var(String)
    instagramPersonalization?: string;

    @Var(String)
    linkedinPersonalization?: string;

    @Var(String)
    googlePersonalization?: string;

    @Var(String)
    facebookSignature?: string;

    @Var(String)
    instagramSignature?: string;

    @Var(String)
    linkedinSignature?: string;

    @Var(String)
    hasReviews: boolean = true;

    @Var(Boolean)
    isReviewsDisplayed: boolean = false;

    @Var(String)
    isReviewsFetched: boolean = false;

    @Var(String)
    isReviewsFetching: boolean = false;

    @Var(String)
    isStatisticsFetching: boolean = false;

    @Var(String)
    chargebeeCustomerId?: string;

    @Var(String)
    contentLanguage: ContentLanguage = ContentLanguage.French;

    @Collection<UserMetadata>({
        namespace: "UserMetadata",
        backlistFields: {
            ownerWorkspaces: true,
            userWorkspaces: true,
            ownerProfiles: true,
            userProfiles: true,
        },
    })
    @SecuritySubCollection({
        get: `${isWorkspaceOrProfileOwnerOrUser} || ${isAdmin}`,
        list: `${isWorkspaceOrProfileOwnerOrUser} || ${isAdmin}`,
        delete: `${isWorkspaceOrProfileOwner} || ${isAdmin}`,
        create: isAdmin,
        update: isAdmin,
    })
    owners = new SubCollection<UserMetadata>();

    /** @Todo sync profile user with usermetadata userProfile if it reuse **/
    @Collection<UserMetadata>({
        namespace: "UserMetadata",
        backlistFields: {
            ownerWorkspaces: true,
            userWorkspaces: true,
            ownerProfiles: true,
            userProfiles: true,
        },
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        delete: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        create: isAdmin,
        update: isAdmin,
    })
    users = new SubCollection<UserMetadata>();

    @Collection({
        namespace: "Post",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser} `,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        create: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        update: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        delete: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
    })
    @SecurityCollectionGroup({
        get: isAdmin,
        list: isAdmin,
        create: isAdmin,
        update: isAdmin,
        delete: isAdmin,
    })
    posts = new SubCollection<Post>();

    @Collection({
        namespace: "SocialProfile",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        create: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        update: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        delete: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
    })
    socialProfiles = new SubCollection<SocialProfile>();

    @Collection({
        namespace: "Task",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        update: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
        create: isAdmin,
    })
    tasks = new SubCollection<Task>();

    @Collection({
        namespace: "Review",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        update: `${isAdmin} || ${isWorkspaceOrProfileOwner}`,
    })
    reviews = new SubCollection<Review>();

    @Collection({
        namespace: "Audit",
    })
    @SecuritySubCollection({
        get: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
        list: `${isAdmin} || ${isWorkspaceOrProfileOwnerOrUser}`,
    })
    audits = new SubCollection<Audit>();

    get isDeleted() {
        return this.deletedAt !== null;
    }
}
